/**
 * FFFilesPanel — Aperçu et gestion des fichiers d'une facture fournisseur.
 *
 * - Liste des fichiers avec preview image / PDF
 * - Toolbar par fichier (rotation, réordo, suppression) si pré-validation
 * - Bouton fusion multi-images en 1 PDF
 * - iter82 — Section "Annexes" : upload/list/delete/reorder + bouton "PDF complet"
 * - Encart "Analyse IA" en bas (résultats OCR)
 */
import { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  FileText, Image as ImageIcon, RotateCw, RotateCcw, ArrowUp, ArrowDown,
  Trash2, FileType, Download, Loader2, Sparkles, Paperclip, Plus, FileArchive,
} from "lucide-react";
import api from "@/lib/api";

// iter81 — Extrait le filename depuis le header Content-Disposition (RFC 6266)
function _filenameFromCD(cd, fallback = "facture.pdf") {
  if (!cd) return fallback;
  // Pattern : filename="..."  ou  filename=...
  const m1 = cd.match(/filename\*=UTF-8''([^;]+)/i);
  if (m1) {
    try { return decodeURIComponent(m1[1]); } catch { /* ignore */ }
  }
  const m2 = cd.match(/filename="?([^";]+)"?/i);
  return m2 ? m2[1].trim() : fallback;
}

// Télécharge un fichier en respectant le filename Content-Disposition du serveur (iter81)
async function _downloadWithServerFilename(url, fallback) {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const cd = r.headers["content-disposition"] || r.headers["Content-Disposition"];
    const filename = _filenameFromCD(cd, fallback);
    const blob = new Blob([r.data], { type: r.headers["content-type"] || "application/pdf" });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(u), 1000);
  } catch (e) {
    toast.error(e.response?.data?.detail || "Erreur téléchargement");
  }
}

function AuthPdfEmbed({ src, fileName }) {
  // iter79 — Charge le PDF en blob auth et l'affiche inline via <iframe>
  // iter91 — Skeleton de chargement + fallback download si iframe échoue
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let active = true;
    let blobUrl = null;
    (async () => {
      try {
        const token = localStorage.getItem("cm_token");
        const r = await fetch(src, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const b = await r.blob();
        blobUrl = URL.createObjectURL(b);
        if (active) setUrl(blobUrl);
      } catch {
        if (active) setErr(true);
      }
    })();
    return () => { active = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [src]);
  if (err) return (
    <div
      data-testid="pdf-viewer-container"
      className="bg-red-50 border border-red-200 p-4 text-center text-xs text-red-700 rounded space-y-2"
    >
      <div>Impossible de charger le PDF dans le navigateur.</div>
      <a
        href={src}
        download={fileName || "facture.pdf"}
        className="inline-block text-red-800 underline"
        data-testid="pdf-viewer-fallback-download"
      >
        Télécharger le fichier
      </a>
    </div>
  );
  if (!url) return (
    <div
      data-testid="pdf-viewer-container"
      className="bg-gray-50 h-64 flex items-center justify-center text-gray-300"
    >
      <Loader2 className="w-6 h-6 animate-spin" />
    </div>
  );
  return (
    <div data-testid="pdf-viewer-container">
      <iframe
        src={url}
        title={fileName || "PDF"}
        className="w-full h-[600px] border-0 bg-gray-50"
        data-testid="ff-pdf-iframe"
      />
    </div>
  );
}

function AuthImage({ src, alt }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let active = true;
    let blobUrl = null;
    (async () => {
      try {
        const token = localStorage.getItem("cm_token");
        const r = await fetch(src, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) throw new Error("load");
        const b = await r.blob();
        blobUrl = URL.createObjectURL(b);
        if (active) setUrl(blobUrl);
      } catch { /* silent */ }
    })();
    return () => { active = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [src]);
  if (!url) return (
    <div className="bg-gray-50 h-48 flex items-center justify-center text-gray-300">
      <Loader2 className="w-5 h-5 animate-spin" />
    </div>
  );
  return <img src={url} alt={alt} className="w-full max-h-[70vh] object-contain bg-gray-50" />;
}

// iter82 — Section dédiée annexes : upload + liste + delete + reorder
function FFAnnexesSection({ id, annexes, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const fileInputRef = useRef(null);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      // 1 label commun pour tous les fichiers du batch (UX simple)
      if (newLabel) {
        const labels = Array.from(files).map(() => newLabel);
        fd.append("labels", JSON.stringify(labels));
      }
      const { data } = await api.post(`/factures-fournisseurs/${id}/annexes`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.ajoutees} annexe(s) ajoutée(s)`);
      setNewLabel("");
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur upload annexe");
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (aid) => {
    if (!window.confirm("Supprimer cette annexe ?")) return;
    setBusy(true);
    try {
      await api.delete(`/factures-fournisseurs/${id}/annexes/${aid}`);
      toast.success("Annexe supprimée");
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur suppression");
    } finally {
      setBusy(false);
    }
  };

  const handleMove = async (idx, direction) => {
    const newOrder = [...annexes];
    const target = idx + direction;
    if (target < 0 || target >= newOrder.length) return;
    [newOrder[idx], newOrder[target]] = [newOrder[target], newOrder[idx]];
    setBusy(true);
    try {
      await api.patch(`/factures-fournisseurs/${id}/annexes/reorder`, {
        ids: newOrder.map((a) => a.id),
      });
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur réordonnancement");
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadAnnexe = (aid, fallbackName) => {
    _downloadWithServerFilename(`/factures-fournisseurs/${id}/annexes/${aid}`, fallbackName);
  };

  return (
    <div className="mt-4 pt-4 border-t border-gray-100" data-testid="ff-annexes-section">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <h4 className="font-semibold text-gray-800 text-sm flex items-center gap-1.5">
          <Paperclip className="w-3.5 h-3.5 text-gray-500" />
          Annexes <span className="text-gray-400 font-normal">({annexes.length})</span>
        </h4>
        <Button
          size="sm" variant="outline" className="text-xs"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          data-testid="ff-annexe-add-btn"
        >
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
          Ajouter une annexe
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.docx,.doc,.xlsx,.xls,.txt"
          className="hidden"
          onChange={(e) => handleUpload(e.target.files)}
          data-testid="ff-annexe-file-input"
        />
      </div>

      {/* Champ label optionnel pour le prochain upload */}
      <Input
        type="text"
        placeholder="Label pour la prochaine annexe (optionnel — ex: Rapport d'expertise)"
        value={newLabel}
        onChange={(e) => setNewLabel(e.target.value)}
        className="text-xs h-7 mb-2"
        data-testid="ff-annexe-label-input"
      />

      {annexes.length === 0 ? (
        <p className="text-gray-400 text-xs italic">
          Aucune annexe. Ajoutez un rapport d&apos;expertise, un bon de livraison, etc.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {annexes.map((ann, idx) => (
            <li
              key={ann.id}
              className="flex items-center gap-1.5 p-2 border border-gray-200 rounded text-xs bg-white"
              data-testid={`ff-annexe-${ann.id}`}
            >
              {/^image\//.test(ann.mime || "") ? (
                <ImageIcon className="w-3.5 h-3.5 text-blue-500 shrink-0" />
              ) : ann.mime === "application/pdf" ? (
                <FileText className="w-3.5 h-3.5 text-red-500 shrink-0" />
              ) : (
                <FileType className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                {ann.label && (
                  <div className="font-medium text-gray-700 truncate" title={ann.label}>
                    {ann.label}
                  </div>
                )}
                <div className="text-gray-500 truncate" title={ann.nom}>
                  {ann.nom} · {Math.round((ann.taille || 0) / 1024)} ko
                </div>
              </div>
              <button
                onClick={() => handleMove(idx, -1)}
                disabled={busy || idx === 0}
                className="text-gray-400 hover:text-gray-700 disabled:opacity-30 p-0.5"
                title="Monter"
                data-testid={`ff-annexe-up-${ann.id}`}
              >
                <ArrowUp className="w-3 h-3" />
              </button>
              <button
                onClick={() => handleMove(idx, 1)}
                disabled={busy || idx === annexes.length - 1}
                className="text-gray-400 hover:text-gray-700 disabled:opacity-30 p-0.5"
                title="Descendre"
                data-testid={`ff-annexe-down-${ann.id}`}
              >
                <ArrowDown className="w-3 h-3" />
              </button>
              <button
                onClick={() => handleDownloadAnnexe(ann.id, ann.nom)}
                className="text-gray-400 hover:text-emerald-700 p-0.5"
                title="Télécharger"
                data-testid={`ff-annexe-download-${ann.id}`}
              >
                <Download className="w-3 h-3" />
              </button>
              <button
                onClick={() => handleDelete(ann.id)}
                disabled={busy}
                className="text-gray-400 hover:text-red-600 p-0.5"
                title="Supprimer"
                data-testid={`ff-annexe-delete-${ann.id}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function FFFilesPanel({ f, id, pageEditable, pageBusy, rotateFile, deleteFile, moveFile, mergeToPdf, onRefresh }) {
  const fichiers = f.fichiers || [];
  const annexes = f.annexes || [];
  const ocr = f.donnees_ocr;
  const imgCount = fichiers.filter((fc) => /^image\//.test(fc.mime || "")).length;

  // iter82 — Télécharge le PDF complet (facture + toutes les annexes mergeables)
  const handleDownloadComplet = () => {
    _downloadWithServerFilename(
      `/factures-fournisseurs/${id}/pdf-complet`,
      `Facture_Complete_${id.slice(0, 8)}.pdf`,
    );
  };

  return (
    <Card className="p-4 lg:sticky lg:top-4 lg:h-fit" data-testid="ff-files-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-500" />
          Pièce(s) jointe(s) <span className="text-gray-400 font-normal text-sm">({fichiers.length})</span>
        </h3>
        <div className="flex gap-1 flex-wrap">
          {(fichiers.length > 0 || annexes.length > 0) && (
            <Button
              size="sm"
              onClick={handleDownloadComplet}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
              data-testid="ff-pdf-complet-btn"
              title="Télécharge un PDF unique fusionnant la facture + toutes les annexes"
            >
              <FileArchive className="w-3.5 h-3.5 mr-1.5" /> PDF complet
            </Button>
          )}
          {pageEditable && imgCount >= 2 && (
            <Button size="sm" variant="outline" onClick={mergeToPdf} disabled={pageBusy} data-testid="ff-merge-pdf-btn" className="text-xs">
              <FileType className="w-3.5 h-3.5 mr-1.5" /> Fusionner en 1 PDF
            </Button>
          )}
        </div>
      </div>

      {fichiers.length === 0 ? (
        <p className="text-gray-400 text-sm italic">Aucun fichier</p>
      ) : (
        <div className="space-y-2">
          {fichiers.map((fic, i) => {
            const isImage = /image\//.test(fic.mime || "");
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/factures-fournisseurs/${id}/file/${i}`;
            return (
              <div key={i} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`ff-file-${i}`}>
                <div className="flex items-center justify-between p-2 bg-gray-50 gap-1 flex-wrap">
                  <span className="text-xs text-gray-600 truncate flex items-center gap-1.5 min-w-0 flex-1">
                    {isImage ? <ImageIcon className="w-3.5 h-3.5 shrink-0" /> : <FileText className="w-3.5 h-3.5 shrink-0" />}
                    <span className="truncate">{fic.nom}</span>
                  </span>
                  <span className="text-[10px] text-gray-400 whitespace-nowrap">{Math.round((fic.taille || 0) / 1024)} ko</span>
                </div>
                {pageEditable && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-white border-b border-gray-100 flex-wrap" data-testid={`ff-file-${i}-toolbar`}>
                    <Button size="sm" variant="ghost" disabled={pageBusy} onClick={() => rotateFile(i, 270)} title="Rotation -90°" className="h-7 px-2" data-testid={`ff-rotate-left-${i}`}>
                      <RotateCcw className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" disabled={pageBusy} onClick={() => rotateFile(i, 90)} title="Rotation +90°" className="h-7 px-2" data-testid={`ff-rotate-right-${i}`}>
                      <RotateCw className="w-3.5 h-3.5" />
                    </Button>
                    <div className="w-px h-4 bg-gray-200 mx-1" />
                    <Button size="sm" variant="ghost" disabled={pageBusy || i === 0} onClick={() => moveFile(i, -1)} title="Monter" className="h-7 px-2" data-testid={`ff-move-up-${i}`}>
                      <ArrowUp className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" disabled={pageBusy || i === fichiers.length - 1} onClick={() => moveFile(i, 1)} title="Descendre" className="h-7 px-2" data-testid={`ff-move-down-${i}`}>
                      <ArrowDown className="w-3.5 h-3.5" />
                    </Button>
                    <div className="w-px h-4 bg-gray-200 mx-1" />
                    <Button size="sm" variant="ghost" disabled={pageBusy || fichiers.length <= 1} onClick={() => deleteFile(i)} title="Supprimer" className="h-7 px-2 text-red-600 hover:bg-red-50" data-testid={`ff-delete-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
                {isImage ? (
                  <AuthImage src={`${url}?variant=preview&t=${fic.taille || 0}`} alt={fic.nom} />
                ) : (
                  <div className="bg-gray-50">
                    {/* iter79 — Affichage inline du PDF + bouton "Ouvrir dans un onglet" */}
                    <AuthPdfEmbed src={`${url}?t=${fic.taille || 0}`} fileName={fic.nom} />
                    <div className="flex items-center justify-between p-2 border-t border-gray-200 bg-white gap-2 flex-wrap">
                      <span className="text-xs text-gray-500 truncate min-w-0 flex-1">
                        PDF · {fic.nom}
                      </span>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          size="sm" variant="outline" className="text-xs"
                          data-testid={`ff-pdf-open-${i}`}
                          onClick={async () => {
                            try {
                              const r = await api.get(`/factures-fournisseurs/${id}/file/${i}`, { responseType: "blob" });
                              const blob = new Blob([r.data], { type: fic.mime || "application/pdf" });
                              const u = URL.createObjectURL(blob);
                              window.open(u, "_blank");
                            } catch { toast.error("Erreur ouverture"); }
                          }}
                        >
                          <FileText className="w-3 h-3 mr-1" /> Ouvrir
                        </Button>
                        <Button
                          size="sm" variant="outline" className="text-xs"
                          data-testid={`ff-pdf-download-${i}`}
                          onClick={() => _downloadWithServerFilename(
                            `/factures-fournisseurs/${id}/file/${i}`,
                            fic.nom || `facture-${id}-${i}.pdf`,
                          )}
                        >
                          <Download className="w-3 h-3 mr-1" /> Télécharger
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* iter82 — Section Annexes */}
      <FFAnnexesSection id={id} annexes={annexes} onRefresh={onRefresh} />

      {/* Analyse IA */}
      {ocr && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="text-xs uppercase tracking-wider text-amber-700 font-semibold mb-2 flex items-center gap-2 flex-wrap">
            <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" /> Analyse IA · confiance {Math.round((ocr.confiance || 0) * 100)} %</span>
            {ocr.qr_detected && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] font-semibold" title="Un QR-bill suisse a été détecté et décodé — les données créancier/IBAN/montant/référence sont byte-perfect, prises directement du QR (zéro erreur OCR possible sur ces champs).">
                ✓ QR-bill lu
              </span>
            )}
          </div>
          <div className="text-xs text-gray-600 grid gap-1">
            {ocr.fournisseur_iban && <div>IBAN détecté : <span className="font-mono">{ocr.fournisseur_iban}</span></div>}
            {ocr.qr_reference && <div>QR-bill : <span className="font-mono text-[10px]">{ocr.qr_reference}</span></div>}
            {ocr.rf_reference && <div>Réf RF : <span className="font-mono">{ocr.rf_reference}</span></div>}
            {ocr.categorie_suggere && <div>Catégorie : <em>{ocr.categorie_suggere}</em></div>}
          </div>
        </div>
      )}
    </Card>
  );
}
